import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, admin, errMsg } from "@/lib/api";
import { ZerodhaTab } from "./ZerodhaTab";
import { ApiKeyTab } from "./ApiKeyTab";
import { NotificationsTab } from "./NotificationsTab";
import { ApiGuideTab } from "./ApiGuideTab";
import { ConfigTab } from "./ConfigTab";
import { MaintenanceTab } from "./MaintenanceTab";

const Unlock = ({ onUnlock }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/verify", null, admin(pw));
      onUnlock(pw);
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 py-4" data-testid="settings-unlock-form">
      <div className="flex items-center gap-3 rounded border border-slate-800 bg-slate-950/50 p-4">
        <Lock className="h-5 w-5 text-cyan-400" />
        <p className="text-sm text-slate-400">Settings hold Zerodha credentials and the master API key. Enter the admin password to view or change them.</p>
      </div>
      <Input data-testid="settings-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
        placeholder="Admin password" className="h-10 border-slate-800 bg-slate-900 font-mono" />
      {err && <p data-testid="settings-unlock-error" className="text-xs text-rose-400">{err}</p>}
      <Button data-testid="settings-unlock-button" disabled={!pw || busy} className="w-full bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock settings"}
      </Button>
    </form>
  );
};

const TRIG = "font-mono text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-300";

export const SettingsDialog = ({ open, onOpenChange }) => {
  const [pw, setPw] = useState(null);
  const close = (o) => {
    if (!o) setPw(null);
    onOpenChange(o);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="settings-dialog" className="scroll-thin max-h-[90vh] max-w-3xl overflow-y-auto overflow-x-hidden border-slate-800 bg-[#0E1420]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-slate-100">Settings</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Password-protected. Unlock is kept only while this window is open.</DialogDescription>
        </DialogHeader>
        {!pw ? (
          <Unlock onUnlock={setPw} />
        ) : (
          <Tabs defaultValue="zerodha" className="mt-2">
            <TabsList className="flex flex-wrap bg-slate-900">
              <TabsTrigger data-testid="settings-tab-zerodha" value="zerodha" className={TRIG}>Zerodha</TabsTrigger>
              <TabsTrigger data-testid="settings-tab-alerts" value="alerts" className={TRIG}>Email alerts</TabsTrigger>
              <TabsTrigger data-testid="settings-tab-apikey" value="apikey" className={TRIG}>Master API key</TabsTrigger>
              <TabsTrigger data-testid="settings-tab-apiguide" value="apiguide" className={TRIG}>API guide</TabsTrigger>
              <TabsTrigger data-testid="settings-tab-config" value="config" className={TRIG}>Watchlists & rules</TabsTrigger>
              <TabsTrigger data-testid="settings-tab-maint" value="maint" className={TRIG}>Maintenance</TabsTrigger>
            </TabsList>
            <TabsContent value="zerodha"><ZerodhaTab pw={pw} /></TabsContent>
            <TabsContent value="alerts"><NotificationsTab pw={pw} /></TabsContent>
            <TabsContent value="apikey"><ApiKeyTab pw={pw} /></TabsContent>
            <TabsContent value="apiguide"><ApiGuideTab /></TabsContent>
            <TabsContent value="config"><ConfigTab pw={pw} /></TabsContent>
            <TabsContent value="maint"><MaintenanceTab pw={pw} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
